import axios, { AxiosResponse } from "axios";
import { useQuery } from "react-query";
import { authHeader } from "../../helpers/authHeader/auth-header";
import { Accessibility } from "../../models/dto/configuration/accessibility.dto";
import { BusTypeDto, BusTypeList } from "../../models/dto/configuration/busType.dto";
import { CancellationFee, CancellationFeeListItemDto } from "../../models/dto/configuration/cancellationFees.dto";
import { SettingItemDto } from "../../models/dto/configuration/generalSettings.dto";
import { LocationBasedPriceSurchargeDto, LocationBasedPriceSurchargeList } from "../../models/dto/configuration/locationBasedPriceSurcharge.dto";
import { BaseListRequest } from "../../models/dto/shared/baseListRequest.dto";
import { CountryDto } from "../../models/dto/shared/country.dto";
const API_URL = process.env.REACT_APP_BASE_API_URL;

const useCancellationFees = () => {
    return useQuery<CancellationFeeListItemDto[]>(
        ['cancellationFees'],
        async () => {
            const res = await axios.get<any, AxiosResponse<CancellationFeeListItemDto[]>>(`${API_URL}/cancellation/fee/all`,
                { headers: authHeader() });
            return res.data;
        },
        { keepPreviousData: true }
    );
}
const createCancellationFee = (cancellationFee: CancellationFee): Promise<CancellationFee> => {
    return axios
        .post<CancellationFee, AxiosResponse<CancellationFee>>(`${API_URL}/cancellation/fee`, cancellationFee, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const updateCancellationFee = (cancellationFee: CancellationFee): Promise<CancellationFee> => {
    return axios
        .put<CancellationFee, AxiosResponse<CancellationFee>>(`${API_URL}/cancellation/fee`, cancellationFee, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const deleteCancellationFee = (id: number) => {
    return axios
        .delete<number>(`${API_URL}/cancellation/fee/${id}`, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getCancellationFee = (id: number): Promise<CancellationFee> => {
    return axios.get<any, AxiosResponse<CancellationFee>>(`${API_URL}/cancellation/fee/${id}`,
        { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const updateAccessibility = (accessibility: Accessibility): Promise<Accessibility> => {
    return axios
        .put<Accessibility, AxiosResponse<Accessibility>>(`${API_URL}/accessibility`, accessibility, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getBusTypes = (pageInfo: BaseListRequest): Promise<BusTypeList> => {
    return axios.get<any, AxiosResponse<BusTypeList>>(`${API_URL}/configuration/bus-type/all`,
        { params: pageInfo, headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getBusType = (id: number): Promise<BusTypeDto> => {
    return axios.get<any, AxiosResponse<BusTypeDto>>(`${API_URL}/configuration/bus-type/${id}`,
        { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const createBusType = (busType: BusTypeDto): Promise<BusTypeDto> => {
    return axios
        .post<BusTypeDto, AxiosResponse<BusTypeDto>>(`${API_URL}/configuration/bus-type`, busType, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const updateBusType = (busType: BusTypeDto): Promise<BusTypeDto> => {
    return axios
        .put<BusTypeDto, AxiosResponse<BusTypeDto>>(`${API_URL}/configuration/bus-type`, busType, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const deleteBusType = (id: number) => {
    return axios
        .delete<number>(`${API_URL}/configuration/bus-type/${id}`, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getLocationBasedPriceSurcharges = (pageInfo: BaseListRequest): Promise<LocationBasedPriceSurchargeList> => {
    return axios.get<any, AxiosResponse<LocationBasedPriceSurchargeList>>(`${API_URL}/configuration/location-based-price-surcharge/all`,
        { params: pageInfo, headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getLocationBasedPriceSurcharge = (id: number): Promise<LocationBasedPriceSurchargeDto> => {
    return axios.get<any, AxiosResponse<LocationBasedPriceSurchargeDto>>(`${API_URL}/configuration/location-based-price-surcharge/${id}`,
        { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const createLocationBasedPriceSurcharge = (item: LocationBasedPriceSurchargeDto): Promise<LocationBasedPriceSurchargeDto> => {
    return axios
        .post<LocationBasedPriceSurchargeDto, AxiosResponse<LocationBasedPriceSurchargeDto>>(`${API_URL}/configuration/location-based-price-surcharge`, item, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const updateLocationBasedPriceSurcharge = (item: LocationBasedPriceSurchargeDto): Promise<LocationBasedPriceSurchargeDto> => {
    return axios
        .put<LocationBasedPriceSurchargeDto, AxiosResponse<LocationBasedPriceSurchargeDto>>(`${API_URL}/configuration/location-based-price-surcharge`, item, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const deleteLocationBasedPriceSurcharge = (id: number) => {
    return axios
        .delete<number>(`${API_URL}/configuration/location-based-price-surcharge/${id}`, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getAccessibility = (companyId?:string): Promise<Accessibility> => {
    return axios
        .get<Accessibility, AxiosResponse<Accessibility>>(`${API_URL}/accessibility`, { params:{ companyId },  headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getCountries = (): Promise<CountryDto[]> => {
    return axios
        .get<null, AxiosResponse<CountryDto[]>>(`${API_URL}/country/all`, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getGeneralSettings = (): Promise<SettingItemDto[]> => {
    return axios
        .get<null, AxiosResponse<SettingItemDto[]>>(`${API_URL}/setting`, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const getSetting = (settingKey: string): Promise<SettingItemDto> => {
    return axios
        .get<string, AxiosResponse<SettingItemDto>>(`${API_URL}/setting/${settingKey}`, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const updateGeneralSettings = (settings: SettingItemDto[]): Promise<string> => {
    return axios
        .put<SettingItemDto[], AxiosResponse<string>>(`${API_URL}/setting`, settings, { headers: authHeader() })
        .then((response) => {
            return response.data;
        });
}
const configurationService = {
    useCancellationFees,
    createCancellationFee,
    getCancellationFee,
    updateCancellationFee,
    deleteCancellationFee,
    updateAccessibility,
    getAccessibility,
    getBusTypes,
    getBusType,
    createBusType,
    updateBusType,
    deleteBusType,
    getLocationBasedPriceSurcharges,
    getLocationBasedPriceSurcharge,
    createLocationBasedPriceSurcharge,
    updateLocationBasedPriceSurcharge,
    deleteLocationBasedPriceSurcharge,
    getCountries,
    getGeneralSettings,
    getSetting,
    updateGeneralSettings
};

export default configurationService;
